import './styles.scss';

import { ExclamationCircleOutlined } from '@ant-design/icons';
import AutoComplete from '@components/AutoComplete/AutoComplete';
import { Button } from '@components/Button/Button';
import NumberRange from '@components/NumberRange/NumberRange';
import { SelectProtocols } from '@components/SelectProtocols/SelectProtocols';
import {
  Cascader,
  Checkbox,
  DatePicker,
  Form,
  FormInstance,
  FormRule,
  Input,
  InputNumber,
  Radio,
  Select,
  Space,
  Switch,
  TimePicker,
  Upload,
  UploadFile,
} from 'antd';
import locale from 'antd/es/date-picker/locale/ru_RU';
import _ from 'lodash';
import React from 'react';
import { Link } from 'react-router';

import { DateTimeFormat } from '@/utils/dateUtils';

export type FormItemType =
  | 'select'
  | 'multiselect'
  | 'groupedSelect'
  | 'protocolsSelect'
  | 'cascader'
  | 'dateTime'
  | 'date'
  | 'dateRange'
  | 'time'
  | 'year'
  | 'text'
  | 'textarea'
  | 'number'
  | 'numberRange'
  | 'numberRangeExtra'
  | 'password'
  | 'switch'
  | 'radioGroup'
  | 'checkbox'
  | 'checkboxGroup'
  | 'label'
  | 'address'
  | 'file'
  | 'navigate'
  | 'autocomplete'
  | 'custom';

export interface IFormItem {
  key: string;
  label?: string;
  type: FormItemType;
  path?: string[];
  placeholder?: string;
  placeholders?: string[];
  default?: any;
  payload?: any;
  validation?: FormItemRule[] | ((form: FormInstance) => FormItemRule[]);
  hidden?: boolean | ((form: FormInstance) => boolean);
  readonly?: boolean;
  disabled?: boolean;
  split?: boolean;
  dependencies?: string[];
  onChange?: (value: any, form: FormInstance) => void;
  extra?: React.ReactNode;
  hideLabel?: boolean;
  noStyle?: boolean;
}

interface IFormItemRenderProps {
  type: FormItemType;
  placeholder?: string;
  placeholders?: string[];
  payload?: any;
  disabled?: boolean;
  onChange: (value: any) => void;
  form: FormInstance;
  key: string;
}

export type FormItemRule = FormRule;

interface IFormItemProps {
  viewMode?: boolean;
  field: IFormItem;
}

export const FormItem: React.FC<IFormItemProps> = ({ viewMode = false, field }) => {
  const {
    key,
    path,
    label,
    validation,
    hidden,
    readonly,
    dependencies,
    extra,
    hideLabel,
    noStyle,
  } = field;

  const form = Form.useFormInstance();

  const getItemClassName = (...args: [boolean, string][]) => {
    return args
      .map((arg) => {
        const [condition, name] = arg;
        return condition ? name : '';
      })
      .filter(Boolean)
      .join(' ');
  };

  return (
    <Form.Item
      key={key}
      name={path ?? key}
      label={hideLabel || !label ? null : <b>{`${label}:`}</b>}
      rules={_.isFunction(validation) ? validation(form) : validation}
      hidden={_.isFunction(hidden) ? hidden(form) : hidden}
      className={getItemClassName(
        [readonly || viewMode, 'read-only-item'],
        [field.type == 'label', 'label-item'],
      )}
      dependencies={dependencies}
      extra={
        extra ? (
          <>
            <ExclamationCircleOutlined size={16} className={'extra-icon'} />
            {extra}
          </>
        ) : undefined
      }
      normalize={(value: any) => (_.isString(value) ? value?.trimStart() : value)}
      noStyle={noStyle}>
      {renderFormItem({
        ...field,
        disabled: field.disabled || viewMode,
        onChange: (value: any) => field.onChange?.(value, form),
        form,
      })}
    </Form.Item>
  );
};

const renderFormItem = ({
  type,
  placeholder,
  placeholders,
  payload,
  disabled,
  onChange,
  form,
  key,
}: IFormItemRenderProps) => {
  const types = {
    select: (
      <Select
        allowClear
        placeholder={placeholder}
        options={payload?.options}
        {...payload}
        disabled={disabled}
        onChange={onChange}
      />
    ),
    multiselect: (
      <Select
        allowClear
        mode="multiple"
        placeholder={placeholder}
        optionFilterProp="label"
        {...payload}
        options={payload?.options}
        disabled={disabled}
        onChange={onChange}
        onDeselect={payload?.onDeselect}
      />
    ),
    groupedSelect: (
      <Select
        allowClear
        labelInValue={payload?.groupInValue}
        placeholder={placeholder}
        disabled={disabled}
        onChange={onChange}
        classNames={{ popup: { root: 'grouped-select-dropdown' } }}>
        {payload?.groups?.map((group: any) => (
          <Select.OptGroup key={group.key} label={group.label}>
            {group.options.length > 0 ? (
              group.options.map((option: any) => (
                <Select.Option key={`${group.key}:${option.value}`} value={option.value}>
                  {option.label}
                </Select.Option>
              ))
            ) : (
              <Select.Option key={`${group.key}:none`} disabled>
                Отсутствуют
              </Select.Option>
            )}
          </Select.OptGroup>
        ))}
      </Select>
    ),
    protocolsSelect: (
      <SelectProtocols
        onChange={onChange}
        form={form}
        disabled={disabled}
        dataRequest={payload?.dataRequest}
      />
    ),
    cascader: (
      <Cascader
        allowClear
        placeholder={placeholder}
        options={payload?.options}
        fieldNames={payload?.fieldNames}
        onChange={onChange}
        showSearch={payload?.showSearch}
        disabled={disabled}
        changeOnSelect
        displayRender={(labels) => labels.at(-1)}
      />
    ),
    dateTime: (
      <DatePicker
        allowClear
        placeholder={placeholder ?? ''}
        format={payload?.format ?? DateTimeFormat.DATE_TIME}
        locale={locale}
        onChange={onChange}
        disabled={disabled}
      />
    ),
    date: (
      <DatePicker
        allowClear
        placeholder={placeholder ?? ''}
        format={payload?.format ?? DateTimeFormat.DATE}
        locale={locale}
        disabledDate={(date) => payload?.disabledDate?.(date, form)}
        onChange={onChange}
        disabled={disabled}
        defaultValue={payload?.defaultValue}
      />
    ),
    dateRange: (
      <DatePicker.RangePicker
        allowClear
        allowEmpty
        placeholder={[placeholders?.[0] ?? 'От', placeholders?.[1] ?? 'До']}
        format={payload?.format ?? DateTimeFormat.DATE}
        locale={locale}
        onChange={onChange}
        disabled={disabled}
        disabledDate={payload?.disabledDate}
        defaultValue={payload?.defaultValue}
      />
    ),
    time: (
      <TimePicker
        allowClear
        placeholder={placeholder}
        format={payload?.format ?? DateTimeFormat.TIME}
        locale={locale}
        onChange={onChange}
        disabled={disabled}
      />
    ),
    year: (
      <DatePicker
        allowClear
        placeholder={placeholder ?? ''}
        format={payload?.format ?? DateTimeFormat.YEAR}
        locale={locale}
        picker="year"
        onChange={onChange}
        disabled={disabled}
      />
    ),
    text: (
      <Input
        allowClear
        placeholder={placeholder}
        onChange={onChange}
        disabled={disabled}
        maxLength={payload?.maxLength}
      />
    ),
    textarea: (
      <Input.TextArea
        allowClear
        rows={2}
        placeholder={placeholder}
        onChange={onChange}
        disabled={disabled}
        autoSize={payload?.autoSize}
      />
    ),
    number: (
      <InputNumber
        placeholder={placeholder}
        min={0}
        max={payload?.max}
        onChange={onChange}
        disabled={disabled}
      />
    ),
    numberRange: (
      <NumberRange
        placeholder={[placeholders?.[0] ?? 'от', placeholders?.[1] ?? 'до']}
        onChange={onChange}
        disabled={disabled}
      />
    ),
    numberRangeExtra: (
      <NumberRange.Composite
        placeholder={[
          placeholders?.[0] ?? 'от',
          placeholders?.[1] ?? 'до',
          placeholders?.[2] ?? 'на',
        ]}
        onChange={onChange}
        disabled={disabled}
      />
    ),
    password: (
      <Input.Password placeholder={placeholder} onChange={onChange} disabled={disabled} />
    ),
    switch: <Switch onChange={onChange} disabled={disabled} />,
    radioGroup: (
      <Radio.Group onChange={onChange} disabled={disabled}>
        <Space direction={payload?.direction ?? 'vertical'}>
          {payload?.options?.map((option: any, index: number) => (
            <Radio key={`${option.label}_${index}`} value={option.value}>
              {option.label}
            </Radio>
          ))}
        </Space>
      </Radio.Group>
    ),
    checkbox: <Checkbox onChange={onChange} disabled={disabled} />,
    checkboxGroup: (
      <Checkbox.Group onChange={onChange} disabled={disabled}>
        <Space direction={payload?.direction ?? 'vertical'}>
          {payload?.options?.map((option: any, index: number) => (
            <Checkbox key={`${option.label}_${index}`} value={option.value}>
              {option.label}
            </Checkbox>
          ))}
        </Space>
      </Checkbox.Group>
    ),
    file: (
      <FileUpload
        accept={payload?.accept}
        placeholder={placeholder}
        disabled={disabled}
        onChange={onChange}
      />
    ),
    navigate: (
      <Link to={payload?.link} style={{ paddingLeft: '11px' }}>
        {form.getFieldValue(key)}
      </Link>
    ),
    autocomplete: (
      <AutoComplete
        placeholder={placeholder}
        disabled={disabled}
        options={payload?.options}
        onSelectOption={payload?.onSelect}
      />
    ),
    label: null,
    address: null,
    // Произвольный компонент поля: Form.Item передаёт ему value/onChange
    custom: payload?.component
      ? React.createElement(payload.component, {
          ...payload?.props,
          disabled,
          onChange,
        })
      : null,
  };
  return types[type];
};

interface IFileUploadProps {
  value?: UploadFile[];
  onChange?: (fileList: UploadFile[]) => void;
  accept?: string;
  placeholder?: string;
  disabled?: boolean;
}

/**
 * Выбор одного файла без загрузки на сервер (beforeUpload → false).
 * Значение поля формы — список файлов antd Upload; сам файл берётся из originFileObj
 * в formFinishValues. Список контролируемый, поэтому resetFields очищает выбор.
 */
const FileUpload = ({
  value,
  onChange,
  accept,
  placeholder,
  disabled,
}: IFileUploadProps) => (
  <Upload
    className={'file-upload-item'}
    maxCount={1}
    multiple={false}
    accept={accept}
    beforeUpload={() => false}
    disabled={disabled}
    fileList={value ?? []}
    showUploadList={{
      showDownloadIcon: false,
      showPreviewIcon: false,
    }}
    onChange={({ fileList }) => onChange?.(fileList)}>
    <Button text={placeholder ?? 'Выберите файл'} disabled={disabled} />
  </Upload>
);
